import { NgClass } from '@angular/common';
import {
    Component,
    DestroyRef,
    effect,
    inject,
    input,
    model,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import type { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { AiChatInputComponent } from '@app/shared/components/ai-chat/ai-chat-input/ai-chat-input.component';
import { AiChatMessageListComponent } from '@app/shared/components/ai-chat/ai-chat-message-list/ai-chat-message-list.component';
import type {
    ChatMessage,
    ChatPart,
    ChatToolState,
    ChatToolStatus,
} from '@app/shared/components/ai-chat/chat-message.model';
import type { ChatModelOption, ChatStreamEvent } from '@app/shared/models/ai-chat.model';
import { AiChatService } from '@app/shared/services/ai-chat.service';
import { AiChatDockService } from '@app/shared/services/ai-chat-dock.service';
import { ClipboardService } from '@app/shared/services/clipboard.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';

type GenericStreamEvent = ChatStreamEvent | { type: string; data?: Record<string, unknown> };

@Component({
    selector: 'cor-ai-chat',
    templateUrl: './ai-chat.component.html',
    styleUrls: ['./ai-chat.component.scss'],
    imports: [NgClass, TooltipModule, AiChatMessageListComponent, AiChatInputComponent],
})
export class AIChatComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly inputRef = viewChild<AiChatInputComponent>(AiChatInputComponent);

    readonly messageToSend = model<string | null>(null);
    readonly newChatRequestId = input(0);
    readonly showCloseButton = input(true);
    readonly isMinimized = model<boolean>(false);
    readonly closeChat = output<void>();
    readonly isLoadingChange = output<boolean>();

    protected readonly botName = inject(AiChatDockService).botName;
    protected readonly messages = signal<ChatMessage[]>([]);
    protected readonly isLoading = signal(false);
    protected readonly selectedModel = signal<ChatModelOption | null>(null);
    protected readonly initialMessages = signal([
        'Summarize what I should know about this workspace.',
        'Help me reason through a platform question.',
        'Draft a concise explanation I can share with my team.',
    ]);

    private readonly aiChatService = inject(AiChatService);
    private readonly clipboardService = inject(ClipboardService);
    private readonly toast = inject(ToastNotificationService);
    private activeSubscription: Subscription | null = null;
    private readonly lastUserMessage = signal<string | null>(null);
    private lastHandledNewChatRequestId = 0;

    constructor() {
        effect(() => {
            const requestId = this.newChatRequestId();
            if (!requestId || requestId === this.lastHandledNewChatRequestId) {
                return;
            }

            this.lastHandledNewChatRequestId = requestId;
            this.clearChat();
        });

        effect(() => {
            const text = this.messageToSend();
            if (!text?.trim()) {
                return;
            }

            if (this.isLoading()) {
                this.inputRef()?.prefill(text);
            } else {
                this.sendMessage(text);
            }

            this.messageToSend.set(null);
        });
    }

    protected onUserSend(text: string): void {
        if (this.isLoading()) {
            return;
        }
        this.sendMessage(text);
    }

    protected onAbort(): void {
        this.activeSubscription?.unsubscribe();
        this.activeSubscription = null;
        this.messages.update((messages: any[]) =>
            messages
                .filter(message => !(message.id === 'streaming-message' && !message.text))
                .map(message =>
                    message.id === 'streaming-message' ? { ...message, id: undefined } : message,
                ),
        );
    }

    protected onInitialMessageClick(text: string): void {
        if (this.isLoading()) {
            return;
        }
        this.sendMessage(text);
    }

    protected onRetry(): void {
        const text = this.lastUserMessage();
        if (!text || this.isLoading()) {
            return;
        }
        this.messages.update((messages: any[]) =>
            messages.filter(message => message.id !== 'error-message'),
        );
        this.sendMessage(text);
    }

    protected onNewChatClick(): void {
        this.clearChat();
    }

    protected onHistoryClick(): void {
        this.toast.showInfo('Chat history is unavailable in this version.');
    }

    protected onMinimizeChatClick(): void {
        this.closeChat.emit();
    }

    clearChat(): void {
        this.activeSubscription?.unsubscribe();
        this.activeSubscription = null;
        this.messages.set([]);
        this.inputRef()?.clear();
        this.setLoading(false);
    }

    protected async copyMessage(text: string): Promise<void> {
        try {
            await this.clipboardService.copyToClipboard(text);
            this.toast.showSuccess('Copied to clipboard.');
        } catch {
            this.toast.showError('Failed to copy message.');
        }
    }

    private sendMessage(text: string): void {
        const message = text.trim();
        if (!message) {
            return;
        }

        this.lastUserMessage.set(message);
        this.messages.update((messages: any) => [
            ...messages,
            { text: message, author: 'user', createdAt: new Date() },
            { id: 'streaming-message', text: '', author: 'bot', createdAt: new Date(), parts: [] },
        ]);
        this.setLoading(true);

        const model = this.selectedModel();
        this.activeSubscription = this.aiChatService
            .stream(message, model?.provider, model?.model)
            .pipe(
                finalize(() => {
                    this.activeSubscription = null;
                    this.setLoading(false);
                    setTimeout(() => this.inputRef()?.focus(), 0);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (event: GenericStreamEvent) => this.applyStreamEvent(event),
                error: () => this.applyRequestError(),
            });
    }

    private setLoading(isLoading: boolean): void {
        this.isLoading.set(isLoading);
        this.isLoadingChange.emit(isLoading);
    }

    private applyStreamEvent(event: GenericStreamEvent): void {
        if (event.type === 'text' && 'text' in event && typeof event.text === 'string') {
            this.appendToAssistant(event.text);

            return;
        }

        if (event.type === 'error') {
            const data = 'data' in event ? event.data : undefined;
            const message =
                ('message' in event && typeof event.message === 'string' && event.message) ||
                (typeof data?.message === 'string' && data.message) ||
                'Sorry, I encountered an error. Please try again.';
            this.replaceStreamingMessage({
                id: 'error-message',
                text: message,
                author: 'bot',
                createdAt: new Date(),
            });

            return;
        }

        if (event.type === 'message.part.delta') {
            this.applyPartDelta(event.data ?? {});
        } else if (event.type === 'message.part.updated') {
            this.applyPartSnapshot(event.data?.part);
        } else if (event.type === 'done') {
            this.finalizeStreamingMessage();
        }
    }

    private appendToAssistant(delta: string): void {
        this.updateStreamingMessage(message => ({
            ...message,
            text: (message.text ?? '') + delta,
            parts: undefined,
        }));
    }

    private applyPartDelta(data: Record<string, unknown>): void {
        const partId = data.partID;
        const delta = data.delta;
        const field = data.field;
        if (typeof partId !== 'string' || typeof delta !== 'string') {
            return;
        }
        if (field !== 'text' && field !== 'reasoning') {
            return;
        }
        const type = field;
        this.upsertPart(partId, existing => {
            const prior =
                existing && (existing.type === 'text' || existing.type === 'reasoning')
                    ? existing.text
                    : '';

            return { id: partId, type, text: prior + delta };
        });
    }

    private applyPartSnapshot(raw: unknown): void {
        if (!raw || typeof raw !== 'object') {
            return;
        }
        const part = raw as Record<string, unknown>;
        const id = part.id;
        const type = part.type;
        if (typeof id !== 'string') {
            return;
        }
        if (type === 'text' || type === 'reasoning') {
            const text = typeof part.text === 'string' ? part.text : '';
            this.upsertPart(id, () => ({ id, type, text }));
        } else if (type === 'tool') {
            const tool = typeof part.tool === 'string' ? part.tool : 'tool';
            const callID = typeof part.callID === 'string' ? part.callID : undefined;
            const state = this.mapToolState(part.state);
            this.upsertPart(id, () => ({ id, type: 'tool', tool, callID, state }));
        }
    }

    private mapToolState(raw: unknown): ChatToolState {
        const state = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
        const status = state.status;
        const known: ChatToolStatus[] = ['pending', 'running', 'completed', 'error'];

        return {
            status: known.includes(status as ChatToolStatus)
                ? (status as ChatToolStatus)
                : 'pending',
            title: typeof state.title === 'string' ? state.title : undefined,
            input:
                state.input && typeof state.input === 'object'
                    ? (state.input as Record<string, unknown>)
                    : undefined,
            output: typeof state.output === 'string' ? state.output : undefined,
            error: typeof state.error === 'string' ? state.error : undefined,
        };
    }

    private upsertPart(partId: string, build: (existing: ChatPart | undefined) => ChatPart): void {
        this.updateStreamingMessage(message => {
            const parts = message.parts ? [...message.parts] : [];
            const index = parts.findIndex(part => part.id === partId);
            const next = build(index >= 0 ? parts[index] : undefined);
            if (index >= 0) {
                parts[index] = next;
            } else {
                parts.push(next);
            }

            return { ...message, parts, text: this.joinTextParts(parts) };
        });
    }

    private joinTextParts(parts: ChatPart[]): string {
        return parts
            .filter((part): part is Extract<ChatPart, { type: 'text' }> => part.type === 'text')
            .map(part => part.text)
            .join('');
    }

    private finalizeStreamingMessage(): void {
        this.updateStreamingMessage(message => ({ ...message, id: undefined }));
    }

    private applyRequestError(): void {
        this.replaceStreamingMessage({
            id: 'error-message',
            text: 'Sorry, I encountered an error. Please try again.',
            author: 'bot',
            createdAt: new Date(),
        });
    }

    private updateStreamingMessage(fn: (message: ChatMessage) => ChatMessage): void {
        this.messages.update((messages: any[]) =>
            messages.map(message => (message.id === 'streaming-message' ? fn(message) : message)),
        );
    }

    private replaceStreamingMessage(message: ChatMessage): void {
        this.messages.update((messages: any[]) =>
            messages.map(current => (current.id === 'streaming-message' ? message : current)),
        );
    }
}
